// Bridges the event modal's Repeat controls to/from an RRULE string. Pure; the
// actual recurrence semantics live in `platform/kit/recurrence.ts`.
import {
  formatRule,
  parseRule,
  type Freq,
  type RecurrenceRule,
  type Weekday,
} from "@/platform/kit";
import { isoDateKey } from "@/platform/client";

export type RepeatMode = "none" | "daily" | "weekly" | "monthly" | "yearly" | "custom";
export type CustomUnit = "day" | "week" | "month" | "year";
export type EndMode = "never" | "on" | "after";

export interface RecurrenceForm {
  mode: RepeatMode;
  interval: number; // custom "every N"
  unit: CustomUnit; // custom
  byDay: Weekday[]; // custom weekly
  endMode: EndMode; // custom
  until: string; // YYYY-MM-DD, when endMode = "on"
  count: number; // when endMode = "after"
}

export const WEEKDAYS: { value: Weekday; label: string }[] = [
  { value: "MO", label: "Mon" },
  { value: "TU", label: "Tue" },
  { value: "WE", label: "Wed" },
  { value: "TH", label: "Thu" },
  { value: "FR", label: "Fri" },
  { value: "SA", label: "Sat" },
  { value: "SU", label: "Sun" },
];

const UNIT_TO_FREQ: Record<CustomUnit, Freq> = {
  day: "DAILY",
  week: "WEEKLY",
  month: "MONTHLY",
  year: "YEARLY",
};
const FREQ_TO_UNIT: Record<Freq, CustomUnit> = {
  DAILY: "day",
  WEEKLY: "week",
  MONTHLY: "month",
  YEARLY: "year",
};
const FREQ_TO_MODE: Record<Freq, RepeatMode> = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  YEARLY: "yearly",
};
const PRESET_TO_FREQ: Record<"daily" | "weekly" | "monthly" | "yearly", Freq> = {
  daily: "DAILY",
  weekly: "WEEKLY",
  monthly: "MONTHLY",
  yearly: "YEARLY",
};

export function emptyRecurrenceForm(): RecurrenceForm {
  return {
    mode: "none",
    interval: 1,
    unit: "week",
    byDay: [],
    endMode: "never",
    until: "",
    count: 10,
  };
}

/** `sDate` (`YYYY-MM-DD`) plus one year, civil-date math (no timezone involved) — the
 *  default "Ends" date offered the moment a user turns repeat on. */
export function defaultUntil(sDate: string): string {
  const [y, m, d] = sDate.split("-").map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  dt.setFullYear(dt.getFullYear() + 1);
  return isoDateKey(dt);
}

/** Parse a stored RRULE into modal fields; a plain FREQ (no interval/byDay) maps to
 *  its preset, anything richer to "custom" — either way the end condition carries over. */
export function recurrenceFormFromRule(rrule: string | null): RecurrenceForm {
  const base = emptyRecurrenceForm();
  if (!rrule) return base;
  const rule = parseRule(rrule);
  if (!rule) return base;
  const endMode: EndMode = rule.count != null ? "after" : rule.until ? "on" : "never";
  const simple = rule.interval === 1 && !rule.byDay?.length;
  if (simple) {
    return {
      ...base,
      mode: FREQ_TO_MODE[rule.freq],
      endMode,
      until: rule.until ?? "",
      count: rule.count ?? base.count,
    };
  }
  return {
    ...base,
    mode: "custom",
    interval: rule.interval,
    unit: FREQ_TO_UNIT[rule.freq],
    byDay: rule.byDay ?? [],
    endMode,
    until: rule.until ?? "",
    count: rule.count ?? base.count,
  };
}

/** Build an RRULE string from modal fields, or null for "no repeat". The end
 *  condition (endMode/until/count) applies to every mode, not just "custom". */
export function recurrenceFormToRule(f: RecurrenceForm): string | null {
  if (f.mode === "none") return null;
  const freq = f.mode === "custom" ? UNIT_TO_FREQ[f.unit] : PRESET_TO_FREQ[f.mode];
  const rule: RecurrenceRule = {
    freq,
    interval: f.mode === "custom" ? Math.max(1, Math.floor(f.interval) || 1) : 1,
  };
  if (f.mode === "custom" && freq === "WEEKLY" && f.byDay.length) rule.byDay = f.byDay;
  if (f.endMode === "on" && f.until) rule.until = f.until;
  else if (f.endMode === "after") rule.count = Math.max(1, Math.floor(f.count) || 1);
  return formatRule(rule);
}
